import React, { useState, useEffect, useCallback } from "react";
import Button from "../Button";
import Cookies from "universal-cookie";
import { EditIcon } from "../../svg";
import TableWrapperHermesSku from "../TableWrapperHermesSku";
import * as endpoints from "../constants/endpoints";
import styled from "styled-components";
import DatepickerWrap from "../DatepickerWrap";
import { toast } from "react-toastify";
import i18next from "i18next";
import { Checkbox, Select, Spin } from "antd";
import "react-datepicker/dist/react-datepicker.css";

const cookies = new Cookies();

interface Props {
  selectedPocMapId?: any;
  closeModal: (shouldUpdate: boolean) => void;
  onDataChanged?: () => void;
  isOpen?: any;
  collection: any[];
  selectedRows?: any;
  modalAction: string;
}

const ModalWrapper = styled.div`
  .search {
    padding-top: 3px;
  }

  .actions {
    display: none;
  }
  .actions button {
    width: 33.33%;
  }
  .actions .react-datepicker-wrapper {
    width: 100%;
  }
`;

const AlternativeCodeSection = styled.div`
  padding: 12px 0;
  border-top: 1px solid #e2e2e2;
  margin-top: 12px;

  .alternative-code-header {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .alternative-code-fields {
    display: flex;
    gap: 12px;
    margin-top: 8px;
    align-items: flex-end;
  }

  .alternative-code-field {
    flex: 1;
  }

  .alternative-code-label {
    font-size: 11px;
    color: #6d6d6d;
    margin-bottom: 4px;
  }
`;

const ModalSkuMapEditActionWrap: React.FC<Props> = ({
  selectedPocMapId,
  closeModal,
  onDataChanged,
  collection,
  selectedRows,
  modalAction,
}) => {
  const [selectedHermesId, setSelectedHermesId] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [disableBtn, setDisableBtn] = useState(false);

  const [alternativeCodeEnabled, setAlternativeCodeEnabled] = useState(false);
  const [alternativeCode, setAlternativeCode] = useState<string | null>(null);
  const [availableCodes, setAvailableCodes] = useState<string[]>([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState(0);
  const [updatingAlternativeCode, setUpdatingAlternativeCode] = useState(false);

  const hasExistingMapping = collection[0].skuMapping !== null;
  const skuMappingId = hasExistingMapping ? collection[0].skuMapping.id : null;
  const originalAlternativeCode = hasExistingMapping
    ? (collection[0].skuMapping.alternativeStockCode ?? "")
    : "";
  const currentEffectiveCode = alternativeCodeEnabled && alternativeCode ? alternativeCode : "";
  const alternativeCodeChanged = currentEffectiveCode !== originalAlternativeCode;

  useEffect(() => {
    if (hasExistingMapping) {
      setDateFrom(collection[0].skuMapping.fromDate ?? "");
      setDateTo(collection[0].skuMapping.toDate ?? "");

      if (collection[0].skuMapping.alternativeStockCode) {
        setAlternativeCodeEnabled(true);
        setAlternativeCode(collection[0].skuMapping.alternativeStockCode);
        fetchAvailableCodes(collection[0].product.id);
      }
    }
  }, []);

  const fetchAvailableCodes = useCallback((productId: number) => {
    setLoadingCodes(true);
    const url = `${endpoints.SKU_MAPPINGS_GET_NON_FREE_CASE_CODES}/${productId}`;
    fetch(url, {
      method: "get",
      headers: {
        Authorization: `Bearer ${cookies.get("rawtoken")}`,
      },
    })
      .then((res) => res.json())
      .then((codes: string[]) => {
        setAvailableCodes(codes);
        setLoadingCodes(false);
      })
      .catch(() => {
        setAvailableCodes([]);
        setLoadingCodes(false);
      });
  }, []);

  const handleAlternativeCodeToggle = useCallback(
    (checked: boolean) => {
      setAlternativeCodeEnabled(checked);
      if (checked) {
        const productId = collection[selectedProductIndex].product.id;
        fetchAvailableCodes(productId);
      } else {
        setAlternativeCode(null);
        setAvailableCodes([]);
      }
    },
    [collection, selectedProductIndex, fetchAvailableCodes],
  );

  const handleProductChange = useCallback(
    (index: number) => {
      setSelectedProductIndex(index);
      setAlternativeCode(null);
      if (alternativeCodeEnabled) {
        const productId = collection[index].product.id;
        fetchAvailableCodes(productId);
      }
    },
    [collection, alternativeCodeEnabled, fetchAvailableCodes],
  );

  const callSetAlternativeCode = useCallback(
    (mappingId: number, code: string) => {
      const params = new URLSearchParams();
      if (code) {
        params.set("alternativeCode", code);
      }
      const url = `${endpoints.SKU_MAPPINGS_SET_ALTERNATIVE_CODE}/${mappingId}?${params.toString()}`;
      return fetch(url, {
        method: "post",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json-patch+json",
          Authorization: `Bearer ${cookies.get("rawtoken")}`,
        },
      });
    },
    [],
  );

  const handleUpdateAlternativeCode = useCallback(() => {
    if (!skuMappingId) return;

    setUpdatingAlternativeCode(true);
    const code = alternativeCodeEnabled && alternativeCode ? alternativeCode : "";

    callSetAlternativeCode(skuMappingId, code)
      .then((res) => {
        setUpdatingAlternativeCode(false);
        if (res.status === 200) {
          toast.success(i18next.t("alternative_code_updated"));
          onDataChanged?.();
        } else {
          toast.error(i18next.t("internal_server_error"));
        }
      })
      .catch(() => {
        setUpdatingAlternativeCode(false);
        toast.error(i18next.t("internal_server_error"));
      });
  }, [skuMappingId, alternativeCodeEnabled, alternativeCode, callSetAlternativeCode, onDataChanged]);

  const getHermesSkuTableSettings = useCallback(() => {
    return {
      tableID: "HermesSKU",
      keyField: "hermesId",
      tableColumns: [
        {
          key: "name",
          title: i18next.t("name"),
          isFilterable: true,
          sortable: true,
        },
        {
          key: "packaging",
          title: i18next.t("packaging"),
          isFilterable: true,
          sortable: true,
        },
        {
          key: "qty",
          title: i18next.t("count"),
          isFilterable: false,
          sortable: false,
        },
        {
          key: "volume",
          title: i18next.t("volume"),
          isFilterable: false,
          sortable: false,
        },
      ],
      tableCells: ["name", "packaging", "quantity", "volume"],
    };
  }, []);

  const emitHermesId = useCallback((hermesId: string) => {
    setSelectedHermesId(hermesId);
  }, []);

  const emitDate = useCallback((value: string, type: string) => {
    if (type === "dateFrom") {
      setDateFrom(value);
    } else {
      setDateTo(value);
    }
  }, []);

  const hasSelectedSku = selectedHermesId !== "" || collection[0].sku !== null;
  const isCreateMode = modalAction === "create";

  const createButtonLabel = (() => {
    if (isCreateMode && alternativeCodeChanged) {
      return i18next.t("create_mapping_and_alternative_code");
    }
    return modalAction === "edit"
      ? i18next.t("edit_mapping")
      : i18next.t("create_mapping");
  })();

  const createMapping = useCallback(() => {
    let action: string;
    let urlAddon = "";
    setDisableBtn(true);

    if (modalAction === "create") {
      action = "POST";
    } else {
      action = "PUT";
      urlAddon = `/${collection[0].skuMapping.id}`;
    }

    const url = `${endpoints.SKU_MAPPINGS_ENDPOINT}${urlAddon}`;

    let skuId: any;
    if (selectedHermesId === "") {
      skuId = collection[0].sku?.id;
    } else {
      skuId = selectedHermesId;
    }

    const altCode = alternativeCodeEnabled && alternativeCode ? alternativeCode : null;

    const bodyCollection = collection.map((item, index) => {
      return {
        skuId: skuId,
        productId: collection[index].product.id,
        depotId: collection[index].product.distributorDepot.id,
        isFreeCase: collection[index].product.hasFreeCase,
        fromDate: dateFrom,
        toDate: dateTo,
        alternativeStockCode: altCode,
      };
    });

    fetch(url, {
      method: action,
      headers: {
        Accept: "*/*",
        "Content-Type": "application/json-patch+json",
        Authorization: `Bearer ${cookies.get("rawtoken")}`,
      },
      body: JSON.stringify(bodyCollection),
    }).then(async (res) => {
      if (res.status === 200) {
        closeModal(true);
        toast.success(i18next.t("successful_mapping"));
      } else if (res.status === 409) {
        setDisableBtn(false);
        toast.error(i18next.t("mapping_conflict"));
      } else {
        setDisableBtn(false);
        toast.error(i18next.t("internal_server_error"));
      }
    });
  }, [
    modalAction,
    collection,
    selectedHermesId,
    dateFrom,
    dateTo,
    alternativeCodeEnabled,
    alternativeCode,
    closeModal,
  ]);

  return (
    <ModalWrapper>
      <div className="actions d-flex align-items-start pt-4 pb-4">
        <Button
          id="edit-map-btn"
          icon={EditIcon}
          size="large"
          variant="outlined"
          onClick={createMapping}
          className="w-50"
          disabled={disableBtn || !hasSelectedSku}
        >
          {createButtonLabel}
        </Button>

        <DatepickerWrap
          dateType={"dateFrom"}
          currentDate={dateFrom}
          emitDate={emitDate}
        />
        <DatepickerWrap
          dateType={"dateTo"}
          currentDate={dateTo}
          emitDate={emitDate}
        />
      </div>

      <AlternativeCodeSection>
        <div className="alternative-code-header">
          <Checkbox
            checked={alternativeCodeEnabled}
            onChange={(e) => handleAlternativeCodeToggle(e.target.checked)}
          >
            {i18next.t("alternative_stock_code")}
          </Checkbox>

          {!isCreateMode && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleUpdateAlternativeCode}
              disabled={updatingAlternativeCode || !skuMappingId || !alternativeCodeChanged || (alternativeCodeEnabled && !alternativeCode)}
            >
              {i18next.t("update")}
            </Button>
          )}
        </div>

        {alternativeCodeEnabled && (
          <div className="alternative-code-fields">
            {collection.length > 1 && (
              <div className="alternative-code-field">
                <div className="alternative-code-label">
                  {i18next.t("product")}
                </div>
                <Select
                  style={{ width: "100%" }}
                  value={selectedProductIndex}
                  onChange={handleProductChange}
                  options={collection.map((item, index) => ({
                    label: `${item.product.code} - ${item.product.description}`,
                    value: index,
                  }))}
                />
              </div>
            )}

            <div className="alternative-code-field">
              <div className="alternative-code-label">
                {i18next.t("code")}
              </div>
              <Select
                style={{ width: "100%" }}
                showSearch
                allowClear
                placeholder={i18next.t("select_code")}
                value={alternativeCode}
                onChange={(value) => setAlternativeCode(value ?? null)}
                loading={loadingCodes}
                disabled={loadingCodes}
                notFoundContent={loadingCodes ? <Spin size="small" /> : null}
                optionFilterProp="label"
                options={availableCodes.map((code) => ({
                  label: code,
                  value: code,
                }))}
              />
            </div>
          </div>
        )}
      </AlternativeCodeSection>

      <TableWrapperHermesSku
        title={i18next.t("hermes_sku")}
        tableSettings={getHermesSkuTableSettings()}
        selectedRows={selectedRows}
        emitHermesId={emitHermesId}
        selectedProductId={
          collection[0].sku ? collection[0].sku.id : 0
        }
      />
    </ModalWrapper>
  );
};

export default ModalSkuMapEditActionWrap;
